import React, { useEffect, useRef, useState } from 'react';
import useCharacterDrafts from './useCharacterDrafts';
import { applyCharacterBuild, characterBuildsEquivalent, serializeCharacterBuild } from './characterBuild';
import CharacterDraftSelector from './CharacterDraftSelector';
import CharacterDraftDialogs from './CharacterDraftDialogs';
import CharacterDraftMenu from './CharacterDraftMenu';

// This coordinator owns intent, not another editable character. Every load
// decision is tied to the entire displayed character and the ready catalog.
// Edits/context changes restart preparation; no old approval authorizes a new build.
export default function CharacterDraftControls({ currentCharacter, setCurrentCharacter, catalog, token,
    saveButton, autosave = false, onToggleAutosave, onDraftSaved }) {
    const drafts = useCharacterDrafts({ characterId: currentCharacter.characterId, token });
    const { refresh, fetchDraft, createDraft, selectDraft } = drafts;
    const [intent, setIntent] = useState(null);
    const [application, setApplication] = useState(null);
    const [dialog, setDialog] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [buildName, setBuildName] = useState('');
    const fingerprint = JSON.stringify(currentCharacter);
    const latest = useRef(null);
    latest.current = { fingerprint, catalog, token, intent, currentCharacter, buildName };
    const mounted = useRef(false);
    const management = useRef(0);
    const mutationActive = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; management.current += 1; };
    }, []);
    useEffect(() => {
        management.current += 1;
        mutationActive.current = false;
        setBuildName('');
        setIntent(null); setApplication(null); setDialog(null); setProcessing(false); setError(null); setNotice(null);
    }, [token]);

    useEffect(() => {
        if (!intent) return;
        // Finish only after React has committed the guarded character update.
        // If another queued edit won that race, prepare a fresh decision instead.
        if (application) {
            setApplication(null);
            if (fingerprint === application.fingerprint) {
                selectDraft(intent.targetId);
                setBuildName(application.name);
                setIntent(null); setDialog(null); setProcessing(false);
            } else {
                setIntent({ targetId: intent.targetId, snapshotName: intent.snapshotName });
            }
            return;
        }
        let cancelled = false;
        const active = () => !cancelled && mounted.current && latest.current.intent === intent &&
            latest.current.token === token && latest.current.fingerprint === fingerprint &&
            latest.current.catalog.ready && latest.current.catalog.skills === catalog.skills;
        setProcessing(true);
        setError(null);
        if (intent.approval && (intent.approval.fingerprint !== fingerprint || intent.approval.skills !== catalog.skills || !catalog.ready)) {
            setDialog(null);
            setIntent({ targetId: intent.targetId, snapshotName: intent.snapshotName });
            return () => { cancelled = true; };
        }
        // An old prompt must disappear while a changed build/context is checked.
        if (intent.approval?.fingerprint !== fingerprint) setDialog(null);
        if (!catalog.ready) return () => { cancelled = true; };
        const fail = result => {
            if (!active()) return;
            const details = Array.isArray(result.validation?.errors)
                ? result.validation.errors.map(item => typeof item?.message === 'string' ? item.message : '').filter(Boolean).join(' ') : '';
            setError([result.error || 'The draft request was superseded. Please try loading again.', details].filter(Boolean).join(' '));
            setIntent(null); setDialog(null); setProcessing(false);
        };
        (async () => {
            try {
                const list = await refresh();
                if (!active()) return;
                if (list.status !== 'success') { fail(list); return; }
                let target = await fetchDraft(intent.targetId);
                if (!active()) return;
                if (target.status !== 'success') { fail(target); return; }
                // Reject malformed purchases before prompting or scheduling a state update.
                serializeCharacterBuild(target.data, catalog.skills);
                const character = latest.current.currentCharacter;
                const matches = list.data.some(draft => characterBuildsEquivalent(character, draft, catalog.skills));
                const approval = intent.approval?.fingerprint === fingerprint ? intent.approval : null;
                if (!matches && !approval) {
                    setDialog({ kind: 'load', target: target.data, fingerprint, snapshotName: intent.snapshotName });
                    setProcessing(false);
                    return;
                }
                if (!matches && approval.choice === 'save') {
                    const saved = await createDraft({ ...serializeCharacterBuild(character, catalog.skills),
                        ...(approval.name ? { name: approval.name } : {}) });
                    if (!active()) return;
                    if (saved.status !== 'success') { fail(saved); return; }
                    setNotice(`Saved draft “${saved.data.name}”.`);
                    onDraftSaved?.(new Date());
                    const refreshed = await refresh();
                    if (!active()) return;
                    if (refreshed.status !== 'success') { fail(refreshed); return; }
                    // Revalidate after persistence: a deleted/invalidated target must
                    // not be applied just because saving the current build succeeded.
                    target = await fetchDraft(intent.targetId);
                    if (!active()) return;
                    if (target.status !== 'success') { fail(target); return; }
                }
                const build = serializeCharacterBuild(target.data, catalog.skills);
                // Calculate once before the guarded updater so malformed input never
                // throws from React's render/update processing.
                const applied = applyCharacterBuild(character, build, catalog.skills);
                if (!active()) return;
                setCurrentCharacter(prev => active() && JSON.stringify(prev) === fingerprint ? applied : prev);
                setApplication({ fingerprint: JSON.stringify(applied), name: target.data.name });
            } catch {
                fail({ error: 'Unable to prepare this build. Check its skills and try again.' });
            }
        })();
        return () => { cancelled = true; };
    }, [intent, application, fingerprint, catalog.ready, catalog.skills, token, refresh, fetchDraft, createDraft, selectDraft, setCurrentCharacter, onDraftSaved]);

    const cancel = () => {
        setIntent(null); setApplication(null); setDialog(null); setProcessing(false); setError(null);
        drafts.selectDraft(drafts.selectedDraftId);
    };
    const actOnDraft = async (action, name) => {
        if (mutationActive.current || intent || dialog || drafts.pending || !mounted.current || latest.current.token !== token) return false;
        return runManagement('create', { name });
    };
    const chooseBuild = async draftId => {
        if (!catalog.ready || intent || dialog || mutationActive.current || drafts.pending) return;
        if (draftId == null) {
            setError(null); setNotice(null);
            selectDraft(null);
            setBuildName('');
            return;
        }
        setError(null); setNotice(null);
        const snapshotName = latest.current.buildName.trim();
        setBuildName(drafts.drafts.find(draft => Number(draft.draftId) === Number(draftId))?.name || '');
        selectDraft(draftId);
        setIntent({ targetId: draftId, snapshotName });
    };
    const deleteBuild = async draftId => {
        const target = drafts.drafts.find(row => Number(row.draftId) === Number(draftId));
        if (!target || intent || dialog || !mounted.current || latest.current.token !== token) return false;
        return runManagement('delete', { target });
    };
    const confirm = ({ choice }) => {
        if (processing || !dialog) return;
        setProcessing(true);
        setIntent({ targetId: dialog.target.draftId, snapshotName: dialog.snapshotName,
            approval: { name: dialog.snapshotName, choice, fingerprint: dialog.fingerprint, skills: catalog.skills } });
    };
    const runManagement = async (kind, { target, name }) => {
        if (mutationActive.current) return false;
        mutationActive.current = true;
        const request = ++management.current;
        const active = () => mounted.current && management.current === request && latest.current.token === token;
        setProcessing(true); setError(null); setNotice(null);
        try {
            let result;
            if (kind === 'create') {
                if (!latest.current.catalog.ready) throw new Error('Catalog not ready');
                const build = serializeCharacterBuild(latest.current.currentCharacter, latest.current.catalog.skills);
                result = await drafts.createDraft({ ...build, ...(name ? { name } : {}) });
            } else result = await drafts.deleteDraft(target.draftId);
            if (!active()) return false;
            if (result.status !== 'success') {
                setError(result.error || 'The draft request was superseded. Choose the draft again before trying again.');
                return false;
            }
            if (kind !== 'delete') {
                onDraftSaved?.(new Date());
                setBuildName(result.data.name);
                latest.current.buildName = result.data.name;
            } else if (Number(drafts.selectedDraftId) === Number(target.draftId)) {
                setBuildName('');
            }
            setNotice(kind === 'delete' ? `Deleted draft “${target.name}”.` :
                `Saved draft “${result.data.name}”.`);
            await drafts.refresh();
            return active();
        } catch {
            if (active()) setError('Unable to complete this draft action. Please try again.');
            return false;
        } finally {
            if (active()) { mutationActive.current = false; setProcessing(false); }
        }
    };
    return <>
        <CharacterDraftMenu modalOpen={!!dialog} saveButton={saveButton}
            autosave={autosave} onToggleAutosave={onToggleAutosave}>
            <CharacterDraftSelector menuStyle {...drafts} catalogReady={catalog.ready}
                buildName={buildName}
                pending={processing || !!intent || !!dialog || !!drafts.pending}
                error={dialog ? null : error || drafts.error} notice={notice}
                onSelect={chooseBuild} onAction={actOnDraft} onDelete={deleteBuild}
                onCancelLoad={intent && !dialog ? cancel : null} />
        </CharacterDraftMenu>
        {dialog && <CharacterDraftDialogs key={`${dialog.kind}-${dialog.target?.draftId || 'new'}-${dialog.fingerprint || ''}`}
            dialog={dialog} pending={processing} error={error || drafts.error} onConfirm={confirm} onCancel={cancel} />}
    </>;
}
